import { t } from '@/i18n';
import { collectSSE, safeJson } from './sse';
import { providerFetch } from './transport';
import { LLMError, type ChatMeta, type ContentPart, type LLMAdapter } from './types';

type GeminiPart = { text: string } | { inlineData: { mimeType: string; data: string } };

function toPart(p: ContentPart): GeminiPart {
  return p.type === 'text' ? { text: p.text } : { inlineData: { mimeType: p.mimeType, data: p.base64 } };
}

export interface GeminiGrounding {
  webSearchQueries?: string[];
  groundingChunks?: { web?: { uri?: string; title?: string } }[];
  searchEntryPoint?: { renderedContent?: string };
}

interface StreamChunk {
  candidates?: { content?: { parts?: { text?: string; thought?: boolean }[] }; groundingMetadata?: GeminiGrounding }[];
  promptFeedback?: { blockReason?: string };
  usageMetadata?: { promptTokenCount?: number; candidatesTokenCount?: number; toolUsePromptTokenCount?: number; thoughtsTokenCount?: number };
}

const NON_CHAT = /embedding|tts|audio|image|imagen|veo|live|aqa|learnlm|robotics/i;

/** Folds one chunk's grounding metadata into the running meta. Pure; exported for tests. */
export function mergeGrounding(meta: ChatMeta, g: GeminiGrounding | undefined): ChatMeta {
  if (!g) return meta;
  const queries = [...(meta.queries ?? [])];
  for (const q of g.webSearchQueries ?? []) if (!queries.includes(q)) queries.push(q);
  const sources = [...(meta.sources ?? [])];
  for (const c of g.groundingChunks ?? []) {
    const url = c.web?.uri;
    if (url && !sources.some((s) => s.url === url)) sources.push({ url, title: c.web?.title });
  }
  return { ...meta, queries, sources, searches: queries.length, searchSuggestionsHtml: g.searchEntryPoint?.renderedContent ?? meta.searchSuggestionsHtml };
}

export const geminiAdapter: LLMAdapter = {
  provider: 'gemini',
  async chat(req) {
    const body = {
      systemInstruction: { parts: [{ text: req.system }] },
      contents: req.messages.map((m) => ({ role: m.role === 'assistant' ? 'model' : 'user', parts: m.content.map(toPart) })),
      generationConfig: req.jsonMode ? { responseMimeType: 'application/json' } : {},
      // Grounding with Google Search: the model decides when to query; results are folded in server-side.
      ...(req.webSearch ? { tools: [{ google_search: {} }] } : {}),
    };
    const res = await providerFetch('gemini', `/v1beta/models/${encodeURIComponent(req.model)}:streamGenerateContent?alt=sse`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body), signal: req.signal }, req.apiKey);
    let meta: ChatMeta = {};
    const text = await collectSSE(res, (data) => {
      const chunk = safeJson(data) as StreamChunk | undefined;
      if (!chunk) return undefined;
      if (chunk.promptFeedback?.blockReason) throw new LLMError('upstream', t('llm.blocked', { reason: chunk.promptFeedback.blockReason }));
      meta = mergeGrounding(meta, chunk.candidates?.[0]?.groundingMetadata);
      if (chunk.usageMetadata) {
        const u = chunk.usageMetadata;
        meta.inputTokens = (u.promptTokenCount ?? 0) + (u.toolUsePromptTokenCount ?? 0);
        meta.outputTokens = (u.candidatesTokenCount ?? 0) + (u.thoughtsTokenCount ?? 0);
      }
      const text = (chunk.candidates?.[0]?.content?.parts ?? []).filter((p) => !p.thought && typeof p.text === 'string').map((p) => p.text).join('');
      return text || undefined;
    }, req.onDelta);
    req.onMeta?.(meta);
    return text;
  },
  async listModels(apiKey, signal) {
    const json = (await (await providerFetch('gemini', '/v1beta/models?pageSize=200', { signal }, apiKey)).json()) as { models?: { name: string; supportedGenerationMethods?: string[] }[] };
    return (json.models ?? [])
      .filter((m) => m.supportedGenerationMethods?.includes('generateContent'))
      .map((m) => m.name.replace(/^models\//, ''))
      .filter((id) => id.startsWith('gemini-') && !NON_CHAT.test(id))
      .sort((a, b) => b.localeCompare(a));
  },
};
